import { useState, useMemo } from "react";
import { HelpCircle, Search, ChevronDown, ChevronUp, Mail, ExternalLink } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { Link } from "react-router-dom";

type FaqItem = {
  question: string;
  answer: string;
};

type GuideLinkItem = {
  type: string;
  title: string;
  description: string;
  href: string;
};

type GuideLinks = {
  title: string;
  items: GuideLinkItem[];
};

type FaqGroup = {
  title: string;
  badge?: string;
  items: FaqItem[];
  guideLinks?: GuideLinks;
};

const FAQ_DATA: FaqGroup[] = [
  {
    title: "Account Management",
    badge: "General",
    items: [
      {
        question: "How do I reset my password?",
        answer: "We don't store your password, so we can't reset it for you. Please folow the password reset guide of your identity provider."
      },
      {
        question: "Can I change my email address?",
        answer: "No, You can't change your email address. If you need to change your email address, please contact your organization admin."
      }
    ]
  },
  {
    title: "Okta Integration",
    badge: "SSO & Identity",
    items: [
      {
        question: "How can I use SmartSite?",
        answer: "Admin must enable SmartSite for your organization. If you are an admin, you can refer the admin guide for SmartSite Okta OIN."
      },
      {
        question: "How long does it take to complete onboarding?",
        answer: "Onboarding usually takes 2-3 days. Please refer the onboarding guide for more details."
      }
    ],
    guideLinks: {
      title: "Detailed Guides",
      items: [
        {
          type: "admin",
          title: "Admin Guide",
          description: "Setup and configuration guide",
          href: "/docs/okta-oin-guide?role=admin"
        },
        {
          type: "user",
          title: "User Guide",
          description: "Learn how to use SmartSite",
          href: "/docs/okta-oin-guide?role=user"
        }
      ]
    }
  }
];

export const FaqPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});

  const toggleItem = (groupIndex: number, itemIndex: number) => {
    const key = `${groupIndex}-${itemIndex}`;
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) return FAQ_DATA;
    const query = searchQuery.toLowerCase();
    return FAQ_DATA.map((group) => ({
      ...group,
      items: group.items.filter(
        item =>
          item.question.toLowerCase().includes(query) ||
          item.answer.toLowerCase().includes(query)
      )
    })).filter(group => group.items.length > 0);
  }, [searchQuery]);

  return (
    <div className="w-full min-h-screen bg-slate-50 dark:bg-slate-950 p-6 lg:p-12 selection:bg-orange-500 selection:text-white">
      <div className="mx-auto max-w-4xl">
        
        {/* Header */}
        <div className="mb-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-50 dark:bg-orange-500/10 text-orange-500 mb-4 shadow-sm"
          >
            <HelpCircle className="w-6 h-6" />
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.3 }}
            className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500"
          >
            Documentation Hub
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.4 }}
            className="text-3xl lg:text-4xl font-extrabold text-slate-900 dark:text-white mt-2 tracking-tight"
          >
            Frequently Asked Questions
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25, duration: 0.4 }}
            className="text-sm text-slate-500 dark:text-slate-400 mt-3 max-w-xl mx-auto"
          >
            Find answers to common questions about federated single sign-on, tenant configuration, and application integrations.
          </motion.p>
        </div>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.4 }}
          className="relative mb-10 max-w-2xl mx-auto"
        >
          <span className="absolute inset-y-0 left-4 flex items-center text-slate-400">
            <Search className="h-5 w-5" />
          </span>
          <input
            type="text"
            className="block w-full pl-12 pr-4 py-3.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl text-slate-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 transition-all placeholder-slate-400 shadow-sm"
            placeholder="Search FAQs (e.g. Okta, Key Vault, tenant)..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </motion.div>

        {/* FAQ Groups */}
        <div className="space-y-10">
          <AnimatePresence mode="popLayout">
            {filteredGroups.length > 0 ? (
              filteredGroups.map((group, gIdx) => (
                <motion.div
                  key={group.title}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-4"
                >
                  <div className="flex items-center gap-3">
                    <h2 className="text-lg font-bold text-slate-950 dark:text-slate-200">
                      {group.title}
                    </h2>
                    {group.badge && (
                      <span className="text-[10px] font-bold uppercase tracking-wider bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 px-2.5 py-0.5 rounded-full">
                        {group.badge}
                      </span>
                    )}
                  </div>

                  <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl overflow-hidden shadow-sm divide-y divide-slate-100 dark:divide-slate-850">
                    {group.items.map((item, iIdx) => {
                      const isOpen = !!openItems[`${gIdx}-${iIdx}`];
                      return (
                        <div key={item.question} className="transition-colors dark:hover:bg-slate-900/40">
                          <button
                            onClick={() => toggleItem(gIdx, iIdx)}
                            className="w-full px-6 py-5 flex justify-between items-start text-left focus:outline-none"
                          >
                            <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 pr-8">
                              {item.question}
                            </span>
                            <span className="flex-shrink-0 mt-0.5 text-slate-400 dark:text-slate-500">
                              {isOpen ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </span>
                          </button>
                          
                          <AnimatePresence initial={false}>
                            {isOpen && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden"
                              >
                                <div className="px-6 pb-6 text-sm text-slate-600 dark:text-slate-400 whitespace-pre-line leading-relaxed">
                                  {item.answer}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>

                  {/* Render guide links if present */}
                  {group.guideLinks && (
                    <div className="mt-4 bg-slate-100/50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 rounded-3xl p-5 space-y-3">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                        {group.guideLinks.title}
                      </h3>
                      <div className="grid gap-4 sm:grid-cols-2">
                        {group.guideLinks.items.map((guide) => (
                          <Link
                            key={guide.type}
                            to={guide.href}
                            className="flex flex-col p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-orange-500/40 dark:hover:border-orange-500/35 hover:shadow-sm rounded-2xl transition-all"
                          >
                            <span className="text-sm font-bold text-slate-950 dark:text-white flex items-center gap-1.5">
                              {guide.title}
                              <ExternalLink className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            </span>
                            <span className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                              {guide.description}
                            </span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              ))
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-16 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-8"
              >
                <p className="text-slate-500 dark:text-slate-400">
                  No matching questions found. Try searching for something else.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Custom Guides Alert */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-12 bg-orange-50/50 dark:bg-orange-950/10 border border-orange-100 dark:border-orange-900/30 rounded-3xl p-6"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="font-bold text-orange-950 dark:text-orange-300">Ready to configure integrations?</h3>
              <p className="text-xs text-orange-700 dark:text-orange-400 mt-1">
                We support Okta Integration Network, Microsoft Entra ID, and custom SSO configurations.
              </p>
            </div>
            <Link
              to="/docs/integrations"
              className="inline-flex items-center gap-1.5 self-start sm:self-center px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-xs font-bold transition-all shadow-sm"
            >
              Explore Integrations
              <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          </div>
        </motion.div>

        {/* Contact Support */}
        <div className="mt-8 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
          <div>
            <h4 className="font-bold text-slate-800 dark:text-slate-200 text-sm">Still have questions?</h4>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Contact our engineering and support desk for integration assistance.
            </p>
          </div>
          <a
            href="mailto:support@nesterra.net"
            className="flex items-center gap-2 text-sm font-semibold text-orange-500 hover:text-orange-600 dark:text-orange-400 transition-colors"
          >
            <Mail className="w-4 h-4" />
            support@nesterra.net
          </a>
        </div>

      </div>
    </div>
  );
};

export default FaqPage;
